import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  CustomerPortalFlatTeamSettingGroupById,
  type CustomerPortalFlatTeamSettingGroupId,
} from "./groups";

type CustomerPortalSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: CustomerPortalSettingInput,
): CustomerPortalSettingInput {
  return metadata;
}

function customerPortalGroup<
  const Group extends CustomerPortalFlatTeamSettingGroupId,
  const T extends Record<string, CustomerPortalSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (or reused keys already
// in FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const CustomerPortalRegistry = {
  ...customerPortalGroup("customer_portal.ai_exchange_agent", {
    "exchangeAgent/enabled": setting({
      label: "Enable AI Exchange Agent",
      description:
        "Encourage customers to make exchanges with personalized content and intelligent product recommendations.",
      synonyms: [
        "ai exchange agent",
        "exchange agent",
        "enable exchange agent",
      ],
    }),
    "exchangeAgent/displayExchangeValueAsDiscount": setting({
      label: "Display exchange value as discount",
      description:
        "Show the exchange value of the returned item as a discount towards other items.",
      synonyms: ["exchange value discount", "display as discount"],
    }),
    "exchangeAgent/variantRecommendations/enabled": setting({
      label: "Enable variant recommendations",
      description:
        "Recommend different variants of the returned item when likely to result in an exchange.",
      synonyms: ["variant recommendations", "recommend variants"],
    }),
    "exchangeAgent/productRecommendations/enabled": setting({
      label: "Enable product recommendations",
      description:
        "Provide intelligent, personalized product recommendations to encourage exchanges.",
      synonyms: ["product recommendations", "recommend products"],
    }),
    "exchangeAgent/productRecommendations/limit": setting({
      label: "Recommendation limit",
      description:
        "Maximum number of product recommendations shown to the customer.",
      synonyms: ["recommendation limit", "max recommendations"],
    }),
    "exchangeAgent/productRecommendations/tagExclusionRules": setting({
      label: "Tag-based exclusion rules",
      description:
        "Rules that block recommendations for products with excluded tags when the returned product matches a source tag.",
      synonyms: [
        "tag exclusion rules",
        "tag-based exclusion rules",
        "exclusion rules",
        "excluded tags",
      ],
    }),
  }),
  ...customerPortalGroup("customer_portal.exchange_settings", {
    "exchanges/variantPickerType": setting({
      label: "Variant picker type",
      description:
        "How shoppers select exchange product variants (manual or auto select).",
      synonyms: ["variant picker", "variant selection"],
    }),
    "exchanges/variantPickerColorHexMetafield": setting({
      label: "Variant picker color hex metafield",
      description:
        "Shopify variant metafield used to render colored circles in the variant picker.",
      synonyms: ["color hex metafield", "variant color metafield"],
    }),
    "exchanges/selection": setting({
      label: "Selection location",
      description:
        "Where to take customers when selecting exchange items (return portal or store site).",
      synonyms: ["selection location", "shop on site selection"],
    }),
    checkoutSelector: setting({
      label: "Checkout selector",
      description:
        "CSS selector of the store's checkout buttons, required for shop on site.",
      synonyms: ["checkout selector", "shop on site selector"],
    }),
    "exchanges/shopSiteURL": setting({
      label: "Product page URL",
      description:
        "Store page customers are directed to when selecting exchange items.",
      synonyms: ["product page url", "shop site url"],
    }),
    exchangeBannerColor: setting({
      label: "Banner background",
      description: "Background color of the shop-on-site exchange banner.",
      synonyms: ["banner background", "banner color"],
    }),
    exchangeBannerFontColor: setting({
      label: "Banner font color",
      description: "Font color of the shop-on-site exchange banner.",
      synonyms: ["banner font color", "banner text color"],
    }),
    exchangeBannerFont: setting({
      label: "Banner font family",
      description: "Font family of the shop-on-site exchange banner.",
      synonyms: ["banner font", "banner font family"],
    }),
    exchangeBannerText: setting({
      label: "Banner text",
      description:
        "Text displayed on the shop-on-site exchange banner. Use %value% for the exchange value.",
      synonyms: ["banner text", "banner message"],
    }),
    exchangeBannerCustomCss: setting({
      label: "Banner custom CSS",
      description: "Custom CSS applied to the shop-on-site exchange banner.",
      synonyms: ["banner custom css", "banner css"],
    }),
    exchangeBannerShowDoneButton: setting({
      label: "Show done button",
      description: "Show a Done button on the shop-on-site exchange banner.",
      synonyms: ["show done button", "done button"],
    }),
    "shopifyExtension/exchangeBannerPositionBottom": setting({
      label: "Exchange banner bottom",
      description: "Show the exchange banner at the bottom of the site.",
      synonyms: ["banner bottom", "banner position"],
    }),
    "shopifyExtension/exchangeBannerInsertBeforeBody": setting({
      label: "Override banner to top",
      description: "Insert the exchange banner at the top of the body.",
      synonyms: ["banner top", "banner override"],
    }),
    "exchanges/exchangeOptionsPreviewShowOriginalPrice": setting({
      label: "Show full price only for exchange options preview",
      description:
        "Show only the full price in the compensation method modal instead of the discounted price.",
      synonyms: ["show full price", "exchange options preview"],
    }),
    exchangeGroups: setting({
      label: "Exchange groups",
      description:
        "Definitions of which products shoppers can exchange into, matched by name, tags, or collections.",
      synonyms: ["exchange groups", "exchange mapping"],
    }),
    "exchanges/allowCustomerDiscountCodes": setting({
      label: "Allow customer discount codes",
      description:
        "Let customers enter discount codes when creating exchanges in the return portal.",
      synonyms: ["customer discount codes", "allow discount codes"],
    }),
    "exchanges/differentPricedVariantExchanges": setting({
      label: "Different-priced variant exchanges",
      description:
        "Charge or refund the price difference for variant exchanges instead of ignoring it.",
      synonyms: ["different priced variants", "price difference exchanges"],
    }),
    "exchanges/variantExchangeProductTags": setting({
      label: "Applied product tags",
      description:
        "Products with these tags have the price difference applied for variant exchanges.",
      synonyms: ["variant exchange product tags", "applied product tags"],
    }),
    "exchanges/excludeDifferentPriceVariants": setting({
      label: "Exclude different-priced variants",
      description:
        "Hide variants priced differently than the returned item from variant exchange options.",
      synonyms: ["exclude different priced variants"],
    }),
    "exchanges/recreateExpiredDiscounts": setting({
      label: "Recreate expired discounts",
      description:
        "Recreate expired original-order discounts as new codes for customer exchanges.",
      synonyms: ["recreate expired discounts"],
    }),
    "exchanges/recreateAutomaticDiscountsAsCodes": setting({
      label: "Recreate automatic discounts as codes",
      description:
        "Recreate an original automatic discount as a code-based discount so it can be reapplied.",
      synonyms: ["recreate automatic discounts"],
    }),
    "exchanges/shopSiteUseCartDiscounts": setting({
      label: "Shop on site cart discounts",
      description:
        "Apply original-order discounts to the store-site cart during shop-on-site exchanges.",
      synonyms: ["shop on site cart discounts", "cart discounts"],
    }),
    "exchanges/applyOriginalOrderDiscounts": setting({
      label: "Original order discounts",
      description:
        "Exchange types for which original-order discounts are carried over to the exchange order.",
      synonyms: ["original order discounts", "apply original discounts"],
    }),
    "exchanges/applyAutomaticDiscounts": setting({
      label: "Automatic discounts",
      description:
        "Exchange types for which automatic discounts are carried over to the exchange order.",
      synonyms: ["automatic discounts", "apply automatic discounts"],
    }),
    "exchanges/reapplyCompareAtDiscount": setting({
      label: "Reapply compare-at discount",
      description:
        "Treat the markdown between a returned item's compare-at price and its sale price as a discount on the exchange order, added to the Shop Now bonus or included with the reapplied discount amount or codes.",
      synonyms: ["compare at price discount", "reapply compare-at markdown"],
    }),
    "exchanges/advancedExchanges": setting({
      label: "New product selection",
      description:
        "Which products are available in the exchange order (any product, up to exchange value, or same product variant).",
      synonyms: ["advanced exchanges", "new product selection"],
    }),
    "exchanges/excludeProductTags": setting({
      label: "Exclude product tags",
      description:
        "Products with these tags are not available when selecting new exchange products.",
      synonyms: ["exchange product tag exclusions"],
    }),
    "exchanges/excludeProductSkus": setting({
      label: "Exclude product SKUs",
      description:
        "Variants with these SKUs are not available when selecting new exchange products.",
      synonyms: ["exclude product skus", "exchange sku exclusions"],
    }),
    "inventory/minimum_stock": setting({
      label: "Minimum inventory",
      description:
        "Inventory quantity at or above which a product counts as in stock in the customer portal.",
      synonyms: ["minimum inventory", "minimum stock"],
    }),
    "exchanges/allowInstant": setting({
      label: "Offer instant exchange",
      description:
        "Let customers opt in to an instant exchange with an order created immediately.",
      synonyms: ["instant exchange", "offer instant exchange"],
    }),
    "exchanges/instantHoldAmount": setting({
      label: "Instant exchange hold amount",
      description:
        "Amount held on the customer's card when they opt in to an instant exchange.",
      synonyms: ["instant hold amount", "exchange hold"],
    }),
    "exchanges/instantExchangeOnly": setting({
      label: "Instant exchange only",
      description:
        "Only allow instant exchanges; regular exchanges are not available.",
      synonyms: ["instant exchange only"],
    }),
    "exchanges/instantExpirationDays": setting({
      label: "Expiration window",
      description:
        "Number of days the customer has to mail original items back before their card is charged.",
      synonyms: ["expiration window", "instant expiration days"],
    }),
    "exchanges/hideOutOfStockVariants": setting({
      label: "Hide out-of-stock variants",
      description:
        "Completely hide out-of-stock variants in the customer portal instead of showing them faded.",
      synonyms: ["hide out of stock variants", "hide out-of-stock variants"],
    }),
    "exchanges/hideUnpublishedVariants": setting({
      label: "Hide unpublished variants",
      description:
        "Hide unpublished products and variants from the exchange catalog. Disable this only when shoppers should be able to select unpublished exchange items.",
      synonyms: ["hide unpublished products", "unpublished variants"],
    }),
  }),
  ...customerPortalGroup("customer_portal.localization", {
    "localization/enabled": setting({
      label: "Enable language support",
      description:
        "Allow customers to change their language in the customer portal.",
      synonyms: ["localization", "language support", "translations"],
    }),
  }),
  ...customerPortalGroup("customer_portal.predefined_categories", {
    "returns/aiReturnReasons/usePredefinedCategories": setting({
      label: "Use predefined categories",
      description:
        "Use the predefined categories for AI return reason categorization and attach their reason codes.",
      synonyms: ["predefined categories", "use predefined categories"],
    }),
    "returns/aiReturnReasons/predefinedCategories": setting({
      label: "Predefined categories",
      description:
        "Predefined return-reason categories with a name and reason code for AI categorization.",
      synonyms: ["predefined categories", "return reason categories"],
    }),
  }),
  ...customerPortalGroup("customer_portal.discount_distribution", {
    discountDistributionMethod: setting({
      label: "Discount distribution",
      description:
        "How BOGO and other discounts are distributed across items during returns.",
      synonyms: ["discount distribution", "distribution method"],
      dependencies: [
        {
          key: "returns/exchanges/orders/shopifyExchangeOrderStrategy",
          relationship: "controlledBy",
          description:
            "Shopify Exchange API is unavailable while order-wide discount distribution is selected, and order-wide distribution is unavailable while Exchange API is selected. Selecting Exchange API resets distribution to Do not distribute.",
        },
      ],
    }),
  }),
  ...customerPortalGroup("customer_portal.bundles", {
    "returns/bundleReturnMode": setting({
      label: "Bundle return behavior",
      description:
        "Whether bundle items are returned separately, together, or by customer choice.",
      synonyms: ["bundle return mode", "bundle return behavior"],
    }),
    "returns/useShopifyNativeBundles": setting({
      label: "Use Shopify native bundles",
      description:
        "Group line items Shopify marks as a native bundle using the bundle return behavior.",
      synonyms: ["shopify native bundles", "native bundles"],
    }),
    "returns/returnTogether": setting({
      label: "Return together properties",
      description:
        "Line-item properties whose shared value causes items to be returned together as a bundle.",
      synonyms: ["return together", "return together properties"],
    }),
  }),
};

type CustomerPortalFlatTeamSettingKey = keyof typeof CustomerPortalRegistry;
const CustomerPortalFlatTeamSettingKeys = Object.keys(
  CustomerPortalRegistry,
) as CustomerPortalFlatTeamSettingKey[];

export const CustomerPortalFlatTeamSettingCatalogDefinitions =
  CustomerPortalFlatTeamSettingKeys.map((key) => {
    const definition = CustomerPortalRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: CustomerPortalFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    CustomerPortalFlatTeamSettingKey,
    CustomerPortalFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<CustomerPortalFlatTeamSettingKey>
  >[];

export const CustomerPortalFlatTeamSettingMetadataByKey = Object.fromEntries(
  CustomerPortalFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<CustomerPortalFlatTeamSettingKey, SettingMetadata>;
